import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { KPICarousel, KPICard } from "../../../Components/KPICarousel";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";
import { SecondsFormatterDetailed } from "../../../Helper/Formatters/SecondsFormatter";
import { getSmartAlertsKPIs } from "../../../Api/SmartAlertsAnalyticsApi";
import CODE from "../../../Static/Constants/StatusCodes";
import CartLoader from "../../../Helper/Loaders/CartLoader";

const calculatePercentageChange = (current, previous, lowerIsBetter = false) => {
  if (!previous || previous === 0) return { value: 0, trend: "neutral", isPositive: true };
  const change = ((current - previous) / previous) * 100;
  let trend;
  let isPositive;
  if (change > 0) {
    trend = "up";
    isPositive = !lowerIsBetter;
  } else if (change < 0) {
    trend = "down";
    isPositive = lowerIsBetter;
  } else {
    trend = "neutral";
    isPositive = true;
  }
  return { value: Math.abs(change).toFixed(1), trend, isPositive };
};

const getTrendPeriodText = (start, end) => {
  if (!start || !end) return "vs. Last 30 Days";
  const startDateObj = new Date(start);
  const endDateObj = new Date(end);
  const today = new Date();
  startDateObj.setHours(0, 0, 0, 0);
  endDateObj.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  if (startDateObj.getTime() === today.getTime() && endDateObj.getTime() === today.getTime()) {
    return "vs. Yesterday";
  }
  const daysDiff = Math.round((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
  return `vs. last ${daysDiff} day${daysDiff === 1 ? "" : "s"}`;
};

const AnalyticsKPISection = ({ startDate, endDate, globalFilters = {}, vehiclesConnected, isLoadingVehiclesCount }) => {
  const { baseEntityTypePluralLable } = useBaseEntityInfo();
  const [kpiData, setKpiData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);

    getSmartAlertsKPIs(globalFilters, { startDate, endDate }).then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (result?.responseStatus?.code !== CODE.SUCCESS) return;

      const d = result.response?.data || {};
      const trendPeriod = getTrendPeriodText(startDate, endDate);

      const totalAlertsChange   = calculatePercentageChange(d.totalAlertsGenerated, d.totalAlertsGeneratedInPreviousTimerange, true);
      const highAlertsChange    = calculatePercentageChange(d.totalHighAlertsGenerated, d.totalHighAlertsGeneratedInPreviousTimerange, true);
      const resolvedAlertsChange = calculatePercentageChange(d.totalAlertsResolved, d.totalAlertsResolvedInPreviousTimerange, false);
      const highResolvedChange  = calculatePercentageChange(d.totalHighAlertsResolved, d.totalHighAlertsResolvedInPreviousTimerange, false);
      const mttrChange          = calculatePercentageChange(d.meanTimeToResolveInSecs, d.meanTimeToResolveInSecsInPreviousTimerange, true);

      setKpiData([
        {
          title: "Total Alerts Triggered",
          titleTooltip: `Total number of alerts triggered by the system across all ${baseEntityTypePluralLable.toLowerCase()} during the selected period.`,
          value: (d.totalAlertsGenerated ?? 0).toString(),
          previousValue: (d.totalAlertsGeneratedInPreviousTimerange ?? 0).toString(),
          trend: totalAlertsChange.trend,
          isPositive: totalAlertsChange.isPositive,
          trendValue: `${totalAlertsChange.value}%`,
          trendPeriod,
          metrics: [
            { label: `${baseEntityTypePluralLable} Affected`, value: (d.totalBatteriesAffected ?? 0).toString() },
            { label: "Avg. Triggers Per Day", value: (d.averageAlertsGeneratedPerDay ?? 0).toString() },
          ],
        },
        {
          title: "High Severity Alerts Triggered",
          titleTooltip: "Total number of alerts marked as High severity — indicating urgent issues that need immediate attention.",
          value: (d.totalHighAlertsGenerated ?? 0).toString(),
          previousValue: (d.totalHighAlertsGeneratedInPreviousTimerange ?? 0).toString(),
          trend: highAlertsChange.trend,
          isPositive: highAlertsChange.isPositive,
          trendValue: `${highAlertsChange.value}%`,
          trendPeriod,
          metrics: [
            { label: `${baseEntityTypePluralLable} Affected`, value: (d.totalHighAlertsBatteriesAffected ?? 0).toString() },
            { label: "Avg. Triggers Per Day", value: (d.averageHighAlertsGeneratedPerDay ?? 0).toString() },
          ],
        },
        {
          title: "Total Alerts Resolved",
          titleTooltip: "Total alerts resolved for all severity — indicating total alerts resolved for the selected time period.",
          value: Math.round(d.totalAlertsResolved ?? 0).toString(),
          previousValue: Math.round(d.totalAlertsResolvedInPreviousTimerange ?? 0).toString(),
          trend: resolvedAlertsChange.trend,
          isPositive: resolvedAlertsChange.isPositive,
          trendValue: `${resolvedAlertsChange.value}%`,
          trendPeriod,
          metrics: [
            { label: `${baseEntityTypePluralLable} Affected`, value: (d.totalAlertsResolvedBatteriesAffected ?? 0).toString() },
            { label: "Avg. Resolved Per Day", value: (d.averageAlertsResolvedPerDay ?? 0).toString() },
          ],
        },
        {
          title: "High Severity Alerts Resolved",
          titleTooltip: "Alerts resolved for high severity — indicating high severity alerts resolved for the selected time period.",
          value: Math.round(d.totalHighAlertsResolved ?? 0).toString(),
          previousValue: Math.round(d.totalHighAlertsResolvedInPreviousTimerange ?? 0).toString(),
          trend: highResolvedChange.trend,
          isPositive: highResolvedChange.isPositive,
          trendValue: `${highResolvedChange.value}%`,
          trendPeriod,
          metrics: [
            { label: `${baseEntityTypePluralLable} Affected`, value: (d.totalHighAlertsResolvedBatteriesAffected ?? 0).toString() },
            { label: "Avg. Resolved Per Day", value: (d.averageHighAlertsResolvedPerDay ?? 0).toString() },
          ],
        },
        {
          title: "Mean Time to Resolve",
          titleTooltip: "Average time taken to resolve alerts from the moment they were raised to when they were marked as resolved — helps assess operational responsiveness.",
          value: SecondsFormatterDetailed(d.meanTimeToResolveInSecs ?? 0, "short"),
          previousValue: SecondsFormatterDetailed(d.meanTimeToResolveInSecsInPreviousTimerange ?? 0, "short"),
          trend: mttrChange.trend,
          isPositive: mttrChange.isPositive,
          trendValue: `${mttrChange.value}%`,
          trendPeriod,
          metrics: [
            { label: "Resolution Rate", value: `${(d.resolutionRatePercent ?? 0).toFixed(1)}%` },
            { label: "MTTR of High Severity Alerts", value: SecondsFormatterDetailed(d.highMeanTimeToResolve ?? 0, "short") },
          ],
        },
      ]);
    });

    return () => { cancelled = true; };
  }, [startDate, endDate, globalFilters, baseEntityTypePluralLable]);

  const renderKPICard = (kpi) => (
    <KPICard
      title={kpi.title}
      titleTooltip={kpi.titleTooltip}
      value={kpi.value}
      previousValue={kpi.previousValue}
      trend={kpi.trend}
      isPositive={kpi.isPositive}
      trendValue={kpi.trendValue}
      trendPeriod={kpi.trendPeriod}
      metrics={kpi.metrics}
      totalVehicles={vehiclesConnected}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="fw400s20c000" sx={{ lineHeight: "28px", fontWeight: 300 }}>
          Key Performance Indicators
        </Typography>
        {isLoadingVehiclesCount ? (
          <Box sx={{ display: "flex", alignItems: "center", height: "16px", px: 2 }}>
            <CartLoader />
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="fw400s16c000000" sx={{ color: "#444B51", lineHeight: "16px" }}>
              {baseEntityTypePluralLable} Connected :
            </Typography>
            <Typography variant="fw400s16c000000" sx={{ fontWeight: 600, color: "#2E2E2E", lineHeight: "16px" }}>
              {vehiclesConnected}
            </Typography>
          </Box>
        )}
      </Box>
      <KPICarousel
        data={kpiData}
        renderCard={renderKPICard}
        loading={loading}
        error={null}
      />
    </Box>
  );
};

export default AnalyticsKPISection;
